import { List } from 'immutable';
import { createSelector } from 'reselect';
import TwoFactorAuthStepOrder from '../constants/TwoFactorAuthStepOrder';
import * as TwoFactorAuthSetupSteps from '../constants/TwoFactorAuthSetupSteps';
import * as TwoFactorAuthDeviceStatuses from '../constants/TwoFactorAuthDeviceStatuses';
import { getTwoFactorConfirmedRequestStatus } from '../selectors/RequestStatusSelector';
import RequestStatusTypes from '../constants/RequestStatusTypes';
import * as TwoFactorAuthMethods from '../constants/TwoFactorAuthMethods';
import TwoFactorAuthSMSErrors from '../constants/TwoFactorAuthSMSErrors';
import * as TwoFactorAuthPrecedenceTypes from '../constants/TwoFactorAuthPrecedenceTypes';
import { getPhoneValidation } from '../selectors/ErrorSelector';
import { getIsUserImpersonated } from './AuthSelector';
const getPersistedStatus = state => state.twoFactorAuth.persistedStatus;
export const getUnusedBackupCodes = createSelector([getPersistedStatus], status => status.get('unusedBackupCodes'));
export const getSetupStep = state => {
  const {
    setupStepIndex
  } = state.twoFactorAuth;
  if (setupStepIndex === -1) {
    return TwoFactorAuthSetupSteps.UNINITIALIZED;
  }
  return TwoFactorAuthStepOrder[state.twoFactorAuth.setupStepIndex];
};
const getBackupCodesResponse = createSelector([getPersistedStatus], status => status.get('backupCodesResponse'));
export const getBackupCodes = createSelector([getBackupCodesResponse], response => {
  return response ? response.get('backupCodes') : List();
});
export const getBackupCodesLoading = state => state.twoFactorAuth.backupCodesLoading;
export const getBackupCodesCreatedAt = createSelector([getBackupCodesResponse], response => {
  return response ? response.get('createdAt') : null;
});
export const getBackupModalVisible = state => state.twoFactorAuth.backupModalVisible;
export const getStagedMethod = state => state.twoFactorAuth.method;
export const getMethodPrecedence = state => state.twoFactorAuth.precedence;
export const getQRCode = state => state.twoFactorAuth.qrCode;
export const getIsHubSpotAppSet = state => {
  return state.twoFactorAuth.isHubSpotAppSet;
};
export const getVerificationCodeAttempt = state => state.twoFactorAuth.verificationCodeAttempt;
export const getVerificationCodeAttemptFailed = state => state.twoFactorAuth.verificationCodeFailure;
export const getStagedSMSNumber = state => state.twoFactorAuth.stagedSMSNumber;
const twoFactorConfirmedRequestStatusIsPending = createSelector([getTwoFactorConfirmedRequestStatus], requestStatus => {
  return requestStatus === RequestStatusTypes.PENDING;
});
const userPrintedOrDownloadedBackupCodes = state => state.twoFactorAuth.printedOrSavedBackupCodes;
export const getStagedConfig = createSelector([getMethodPrecedence, getPersistedStatus], (precedence, persistedStatus) => {
  const level = precedence === TwoFactorAuthPrecedenceTypes.SECONDARY ? 'secondary' : 'primary';
  return persistedStatus.getIn([level]);
});
const getPrimaryMethodStatus = createSelector([getPersistedStatus], persistedStatus => persistedStatus.getIn(['primary', 'status']));
const getSecondaryMethodStatus = createSelector([getPersistedStatus], persistedStatus => persistedStatus.getIn(['secondary', 'status']));
export const getPrimaryAuthMethod = createSelector([getPersistedStatus, getPrimaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['primary', 'method']) : null;
});
export const getPrimaryAuthInfo = createSelector([getPersistedStatus, getPrimaryAuthMethod], (persistedStatus, primaryAuthMethod) => {
  return primaryAuthMethod === TwoFactorAuthMethods.SMS ? persistedStatus.getIn(['primary', 'info']) : null;
});
export const getSecondaryAuthMethod = createSelector([getPersistedStatus, getSecondaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['secondary', 'method']) : null;
});
export const getShouldSetupSecondaryAuthMethod = createSelector([getSetupStep, getPrimaryMethodStatus, getSecondaryMethodStatus], (step, primaryMethodStatus, secondaryMethodStatus) => {
  return step === TwoFactorAuthSetupSteps.VERIFICATION_SUCCESSFUL && primaryMethodStatus === TwoFactorAuthDeviceStatuses.CONFIRMED && secondaryMethodStatus !== TwoFactorAuthDeviceStatuses.CONFIRMED;
});
export const getSetupForLoginAccountInfo = state => state.twoFactorAuth.setupForLoginAccountInfo;
export const getSetupForLoginPortalId = createSelector([getSetupForLoginAccountInfo], accountInfo => accountInfo.get('portalId'));
export const isSetupForLogin = state => state.twoFactorAuth.setupForLogin;
const getPrimaryInfo = createSelector([getPersistedStatus, getPrimaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['primary', 'info']) : null;
});
const getSecondaryInfo = createSelector([getPersistedStatus, getSecondaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['secondary', 'info']) : null;
});
const getConfiguredSMSNumbers = createSelector([getPrimaryAuthMethod, getPrimaryInfo, getSecondaryAuthMethod, getSecondaryInfo], (primaryMethod, primaryInfo, secondaryMethod, secondaryInfo) => {
  const used = [];
  if (primaryMethod === TwoFactorAuthMethods.SMS) {
    used.push(primaryInfo);
  }
  if (secondaryMethod === TwoFactorAuthMethods.SMS) {
    used.push(secondaryInfo);
  }
  return used;
});
const getIsTrustedNumberSetup = state => {
  const {
    setupStepIndex
  } = state.securityPhoneNumberSetup;
  if (setupStepIndex === -1) {
    return false;
  }
  return true;
};
const isErrorSendingSMS = state => state.twoFactorAuth.isErrorSendingSMS;
const isTrustedNumberError = state => state.securityPhoneNumberSetup.isErrorSendingSMS;
export const getSMSRegisterError = createSelector([isErrorSendingSMS, getConfiguredSMSNumbers, getStagedSMSNumber, getPhoneValidation, getIsTrustedNumberSetup, isTrustedNumberError], (errorSendingSMS, usedNumbers, stagedSMSNumber, phoneValidation, isTrustedNumberSetup, trustedNumberError) => {
  if (phoneValidation && phoneValidation === TwoFactorAuthSMSErrors.UNSUPPORTED_COUNTRY) {
    return TwoFactorAuthSMSErrors.UNSUPPORTED_COUNTRY;
  }
  if (phoneValidation && phoneValidation === TwoFactorAuthSMSErrors.NOT_VALID_NUMBER) {
    return TwoFactorAuthSMSErrors.SMS_NOT_SENT;
  }
  if (errorSendingSMS || trustedNumberError) {
    return TwoFactorAuthSMSErrors.SMS_NOT_SENT;
  }
  if (usedNumbers.indexOf(stagedSMSNumber) >= 0 && !isTrustedNumberSetup) {
    return TwoFactorAuthSMSErrors.NUMBER_ALREADY_REGISTERED;
  }
  return undefined;
});
export const getShouldBlockSetupProgress = createSelector([getVerificationCodeAttempt, twoFactorConfirmedRequestStatusIsPending, getSetupStep, getSMSRegisterError, userPrintedOrDownloadedBackupCodes, getPhoneValidation, getStagedSMSNumber, getIsHubSpotAppSet], (verificationCodeAttempt, isPending, setupStep, smsRegisterError, printedOrSavedBackupCodes, phoneValidation, stagedSMSNumber, isHubSpotAppSet) => {
  switch (setupStep) {
    case TwoFactorAuthSetupSteps.VERIFY_SMS:
    case TwoFactorAuthSetupSteps.VERIFY_GOOGLE_AUTHENTICATOR:
      return verificationCodeAttempt === '';
    case TwoFactorAuthSetupSteps.CHOOSE_METHOD:
    case TwoFactorAuthSetupSteps.REGISTER_SMS:
      if ([TwoFactorAuthSMSErrors.UNSUPPORTED_COUNTRY, TwoFactorAuthSMSErrors.NOT_VALID_NUMBER].indexOf(phoneValidation) >= 0 || stagedSMSNumber.length < 4) {
        return true;
      }
      return smsRegisterError !== undefined;
    case TwoFactorAuthSetupSteps.BACKUP_CODES:
      return !printedOrSavedBackupCodes;
    case TwoFactorAuthSetupSteps.REGISTER_WITH_HUBSPOT_APP:
      return !isHubSpotAppSet;
    default:
      return isPending;
  }
});
export const getShouldBlockVerifyButton = createSelector([getPhoneValidation, getStagedSMSNumber], (phoneValidation, stagedSMSNumber) => {
  if ([TwoFactorAuthSMSErrors.UNSUPPORTED_COUNTRY, TwoFactorAuthSMSErrors.NOT_VALID_NUMBER].indexOf(phoneValidation) >= 0 || stagedSMSNumber.length < 4) {
    return true;
  }
  return false;
});
export const getShouldRenderGoogleAuthWarning = createSelector([getPrimaryAuthMethod, getStagedMethod], (primaryAuthMethod, stagedMethod) => {
  return stagedMethod === TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR && primaryAuthMethod === TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR;
});
export const getShouldNotSetupOnLogin = createSelector([isSetupForLogin, getPrimaryAuthMethod, getSetupStep], (setupForLogin, primaryAuthMethod, setupStep) => setupForLogin && primaryAuthMethod !== null && setupStep === TwoFactorAuthSetupSteps.CHOOSE_METHOD);
export const getUserLoginRequired = state => state.twoFactorAuth.requiresUserToLogin;
export const getCanModify2FASettings = createSelector([getUserLoginRequired, getIsUserImpersonated], (userLoginRequired, isUserImpersonated) => !userLoginRequired && !isUserImpersonated);
export const getRedirectUrl = state => state.twoFactorAuth.redirectUrl;
export const getShow2FANudge = state => state.twoFactorAuth.show2FANude;
export const is2FANudge = state => state.twoFactorAuth.setupFromNudge;
export const getHsMobileAppVersionStatus = state => state.twoFactorAuth.hsMobileAppVersionStatus;
export const getHsMobileAppVersionError = createSelector([getHsMobileAppVersionStatus], versionStatus => versionStatus === RequestStatusTypes.FAILED);
export const getHsMobileAppVersionSuccess = createSelector([getHsMobileAppVersionStatus], versionStatus => versionStatus !== RequestStatusTypes.FAILED);